import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, FileText, X, RefreshCw, ChevronRight } from 'lucide-react';
import { TopBar } from '../components/TopBar';
import { Footer } from '../components/Footer';
import { ScorePill } from '../components/ScorePill';
import { ShortcutsModal } from '../components/ShortcutsModal';
import { useTheme } from '../hooks/useTheme';
import { relativeDate, formatScore } from '../lib/format';
import { getUserId, ownerHash } from '../lib/userId';
import { listResults, type LibraryEntry, AnalyseError } from '../lib/api';
import { SECTOR_LABELS, type Sector, type Verdict } from '../lib/types';
import styles from './HistoryScreen.module.css';

const VERDICTS: Verdict[] = ['BULLISH', 'MIXED', 'BEARISH'];
const PAGE_SIZE = 50;

type Scope = 'all' | 'mine';

function formatPeriod(p: string | undefined): string {
  if (!p) return '';
  const m = /^(\d{4})-(Q[1-4]|FY)$/.exec(p);
  if (!m) return p;
  return m[2] === 'FY' ? `${m[1]} FY` : `${m[2]} ${m[1]}`;
}

export function HistoryScreen() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven filter state so links/back-button preserve the current view.
  const scope = (searchParams.get('scope') === 'mine' ? 'mine' : 'all') as Scope;
  const verdictFilter = (searchParams.get('verdict') ?? 'ALL') as Verdict | 'ALL';
  const sectorFilter = (searchParams.get('sector') ?? 'ALL') as Sector | 'ALL';
  const query = searchParams.get('q') ?? '';

  // Server data.
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const myHash = useMemo(() => ownerHash(getUserId()), []);

  // Reset and fetch the first page whenever filters change.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    setEntries([]);
    setOffset(0);

    listResults({
      mineOnly: scope === 'mine',
      verdict: verdictFilter === 'ALL' ? undefined : verdictFilter,
      sector: sectorFilter === 'ALL' ? undefined : sectorFilter,
      q: query.trim() || undefined,
      limit: PAGE_SIZE,
      offset: 0,
      signal: ctrl.signal,
    })
      .then((page) => {
        if (cancelled) return;
        setEntries(page.entries);
        setTotal(page.total);
        setOffset(page.entries.length);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled || (err as { name?: string }).name === 'AbortError') return;
        const msg = err instanceof AnalyseError ? err.message : 'Could not load library.';
        setError(msg);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [scope, verdictFilter, sectorFilter, query]);

  function loadMore() {
    if (appending || loading || entries.length >= total) return;
    setAppending(true);
    listResults({
      mineOnly: scope === 'mine',
      verdict: verdictFilter === 'ALL' ? undefined : verdictFilter,
      sector: sectorFilter === 'ALL' ? undefined : sectorFilter,
      q: query.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((page) => {
        setEntries((prev) => [...prev, ...page.entries]);
        setOffset((prev) => prev + page.entries.length);
        setTotal(page.total);
        setAppending(false);
      })
      .catch((err) => {
        const msg = err instanceof AnalyseError ? err.message : 'Could not load more.';
        setError(msg);
        setAppending(false);
      });
  }

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === '' || value === 'ALL') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  const visibleSectorOptions = useMemo(() => {
    return Object.entries(SECTOR_LABELS) as [Sector, string][];
  }, []);

  return (
    <div className={styles.page}>
      <TopBar
        historyCount={0}
        theme={theme}
        onShortcuts={() => setShortcutsOpen(true)}
        onToggleTheme={toggle}
      />

      <main className={styles.main} id="main">
        <header className={styles.header}>
          <span className={styles.eyebrow}>Library</span>
          <h1 className={styles.headline}>
            {loading ? 'Loading library…' : `${total} analysed document${total === 1 ? '' : 's'}`}
          </h1>
          <p className={styles.sub}>
            Every analysis run on TheInvestmentAnalyst is saved here. Search, filter,
            or jump back into one — including past work from other users.
          </p>
        </header>

        <div className={styles.controls}>
          <div className={styles.searchWrap}>
            <Search size={16} strokeWidth={1.5} className={styles.searchIcon} aria-hidden="true" />
            <input
              type="search"
              className={styles.search}
              placeholder="Search title, headline, ticker…"
              value={query}
              onChange={(e) => setParam('q', e.target.value)}
              aria-label="Search library"
            />
            {query && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => setParam('q', null)}
                aria-label="Clear search"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className={styles.filterGroup} role="radiogroup" aria-label="Scope">
            {(['all', 'mine'] as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={scope === s}
                className={[styles.filterBtn, scope === s ? styles.filterActive : ''].join(' ')}
                onClick={() => setParam('scope', s === 'all' ? null : s)}
              >
                {s === 'all' ? 'Everyone' : 'Mine'}
              </button>
            ))}
          </div>

          <div className={styles.filterGroup} role="radiogroup" aria-label="Filter by verdict">
            {(['ALL', ...VERDICTS] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={verdictFilter === v}
                className={[styles.filterBtn, verdictFilter === v ? styles.filterActive : ''].join(' ')}
                onClick={() => setParam('verdict', v === 'ALL' ? null : v)}
              >
                {v === 'ALL' ? 'All' : v}
              </button>
            ))}
          </div>

          <select
            className={styles.sectorSelect}
            value={sectorFilter}
            onChange={(e) => setParam('sector', e.target.value === 'ALL' ? null : e.target.value)}
            aria-label="Filter by sector"
          >
            <option value="ALL">All sectors</option>
            {visibleSectorOptions.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className={styles.errorBox} role="alert">
            <span>{error}</span>
            <button
              type="button"
              className={styles.retryBtn}
              onClick={() => setParam('q', query)}
            >
              <RefreshCw size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>Retry</span>
            </button>
          </div>
        )}

        {loading ? (
          <ul className={styles.list} aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className={styles.item}>
                <div className={[styles.itemBtn, styles.skeleton].join(' ')} aria-hidden="true" />
              </li>
            ))}
          </ul>
        ) : entries.length === 0 ? (
          <div className={styles.empty}>
            <FileText size={32} strokeWidth={1.5} aria-hidden="true" className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>
              {scope === 'mine' && total === 0 && !query && verdictFilter === 'ALL' && sectorFilter === 'ALL'
                ? "You haven't run any analyses yet."
                : 'No matches.'}
            </p>
            <p className={styles.emptyHint}>
              {scope === 'mine' && total === 0 && !query && verdictFilter === 'ALL' && sectorFilter === 'ALL'
                ? <>Run one on the <Link to="/" className={styles.link}>input screen</Link> and it'll show up here.</>
                : 'Try clearing a filter or switching to "Everyone".'}
            </p>
          </div>
        ) : (
          <>
            <ul className={styles.list}>
              {entries.map((it) => {
                const isMine = it.ownerHash && it.ownerHash === myHash;
                const periodLabel = formatPeriod(it.period);
                const sectorLabel = it.sector ? SECTOR_LABELS[it.sector] : '';
                const subtitle = it.headline || '';
                const chips = [it.ticker, periodLabel, sectorLabel].filter(Boolean) as string[];
                return (
                  <li key={it.slug} className={styles.item}>
                    <button
                      type="button"
                      className={styles.itemBtn}
                      onClick={() => navigate(`/result/${it.slug}`)}
                    >
                      <div className={styles.itemMain}>
                        <span className={styles.itemTitleRow}>
                          <span className={styles.itemTitle}>{it.title}</span>
                          {isMine && <span className={styles.mineTag}>MINE</span>}
                        </span>
                        {subtitle && <span className={styles.itemSubtitle}>{subtitle}</span>}
                        <span className={styles.itemMeta}>
                          {chips.map((c) => (
                            <span key={c} className={styles.chip}>{c}</span>
                          ))}
                          <time dateTime={it.date}>{relativeDate(it.date)}</time>
                          <span className={['mono', styles.itemScore].join(' ')}>
                            {formatScore(it.score)}
                          </span>
                        </span>
                      </div>
                      <span className={styles.itemRight}>
                        <ScorePill verdict={it.verdict} size="sm" />
                        <ChevronRight size={16} strokeWidth={1.5} className={styles.chevron} aria-hidden="true" />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {entries.length < total && (
              <div className={styles.loadMoreRow}>
                <button
                  type="button"
                  className={styles.loadMore}
                  onClick={loadMore}
                  disabled={appending}
                >
                  {appending ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, total - entries.length)} more`}
                </button>
                <span className={styles.loadMoreNote}>
                  Showing {entries.length} of {total}
                </span>
              </div>
            )}
          </>
        )}
      </main>

      <Footer onShortcuts={() => setShortcutsOpen(true)} />
      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
